import { createColumnHelper } from '@tanstack/react-table';
import { FormattedDateTime, Text, type DataTableFeatures } from '@customerio/pluma-components/react';
import { isEmptyString } from '@/utils/string';
import type { WorkspaceObjectType } from '@/features/workspaces';
import { isObjectLog, NameCell, ObjectCell, PersonCell, TypeCell } from './cells';
import type { IdentifierConfig } from '../display-name';
import type { ActivityLogRow } from '../types';

const helper = createColumnHelper<DataTableFeatures, ActivityLogRow>();

/** Resolves a row's human-readable Activity Type label (taxonomy-aware; supplied by the page). */
export type ResolveTypeLabel = (row: ActivityLogRow) => string;

/** Dependencies the identified columns need from the page (taxonomy + workspace-scoped links). */
export interface IdentifiedColumnDeps {
  resolveTypeLabel: ResolveTypeLabel;
  workspaceId: string;
  personSingular: string;
  objectTypeById: Map<number, WorkspaceObjectType>;
  /** Builder-plan accounts have no objects, so the header is just the person term. */
  isBuilder: boolean;
  /** Workspace identifier config, for the Activity Name `(N identifier)` count. */
  identifiers: IdentifierConfig;
}

type TimestampCellProps = { value: number | null | undefined };

function TimestampCell({ value }: TimestampCellProps) {
  if (value == null)
    return (
      <Text size="xxs" color="subtle">
        —
      </Text>
    );
  // Relative time + hover tooltip with the absolute date/time; FormattedDateTime auto-detects
  // epoch seconds vs ms.
  return (
    <FormattedDateTime
      date={value}
      format="medium_with_time"
      withRelativeDateFromNow
      withTooltip
      size="xxs"
      color="subtle"
    />
  );
}

/**
 * Identified tab: {Person|Profile}/Object · Activity Type · Activity Name · Timestamp · Processed At.
 */
export function createIdentifiedColumns({
  resolveTypeLabel,
  workspaceId,
  personSingular,
  objectTypeById,
  isBuilder,
  identifiers,
}: IdentifiedColumnDeps) {
  // The person term, suffixed with `/Object` unless the account is on the Builder plan (which has
  // no objects). The term follows the taxonomy rename (Person → "Profile"), same as the page title.
  const personObjectHeader = isBuilder ? personSingular : `${personSingular}/object`;
  return [
    helper.display({
      id: 'person',
      header: personObjectHeader,
      cell: (info) =>
        isObjectLog(info.row.original.log) ? (
          <ObjectCell row={info.row.original} workspaceId={workspaceId} objectTypeById={objectTypeById} />
        ) : (
          <PersonCell row={info.row.original} workspaceId={workspaceId} personSingular={personSingular} />
        ),
    }),
    helper.display({
      id: 'type',
      header: 'Activity type',
      cell: (info) => (
        <TypeCell
          row={info.row.original}
          label={resolveTypeLabel(info.row.original)}
          workspaceId={workspaceId}
          personSingular={personSingular}
        />
      ),
    }),
    helper.display({
      id: 'name',
      header: 'Activity name',
      cell: (info) => <NameCell row={info.row.original} workspaceId={workspaceId} identifiers={identifiers} />,
    }),
    helper.accessor((row) => row.log.timestamp, {
      id: 'timestamp',
      header: 'Timestamp',
      cell: (info) => <TimestampCell value={info.getValue()} />,
    }),
    helper.accessor((row) => row.log.processed_at, {
      id: 'processed_at',
      header: 'Processed at',
      cell: (info) => <TimestampCell value={info.getValue()} />,
    }),
  ];
}

/** Anonymous tab: Anonymous ID · Type · Name · Timestamp (no Person, no Processed At). */
export function createAnonymousColumns({
  resolveTypeLabel,
  workspaceId,
  personSingular,
}: {
  resolveTypeLabel: ResolveTypeLabel;
  workspaceId: string;
  personSingular: string;
}) {
  return [
    helper.accessor((row) => row.log.customer_id, {
      id: 'anonymous_id',
      header: 'Anonymous ID',
      cell: (info) => <Text size="sm">{isEmptyString(info.getValue() ?? '') ? '—' : info.getValue()}</Text>,
    }),
    helper.display({
      id: 'type',
      header: 'Type',
      cell: (info) => (
        <TypeCell
          row={info.row.original}
          label={resolveTypeLabel(info.row.original)}
          workspaceId={workspaceId}
          personSingular={personSingular}
        />
      ),
    }),
    helper.display({
      id: 'name',
      header: 'Name',
      cell: (info) => <NameCell row={info.row.original} workspaceId={workspaceId} />,
    }),
    helper.accessor((row) => row.log.timestamp, {
      id: 'timestamp',
      header: 'Timestamp',
      cell: (info) => <TimestampCell value={info.getValue()} />,
    }),
  ];
}
